import {
  SPEC_PROPERTIES,
  TEXT_LIMIT_FIELDS,
  type SpecMediaType,
  type TextLimitField,
} from '@inference.engine/ad-formats/web';
import type {
  LibraryFormat,
  LibrarySpecValue,
  LibraryState,
  LibraryTextLimit,
} from '../model/types';

export interface SpecRow {
  property: string;
  /** The format's own value, which wins over the profile's. */
  own: LibrarySpecValue | undefined;
  /** The value the format's spec profile gives, shown when the format has none of its own. */
  inherited: LibrarySpecValue | undefined;
}

/** Every registry property of one media type, with what the format and its profile say about it. */
export function specRows(
  state: LibraryState,
  format: LibraryFormat,
  mediaType: SpecMediaType,
): SpecRow[] {
  const find = (ownerType: 'format' | 'profile', ownerKey: string | null, property: string) =>
    ownerKey === null
      ? undefined
      : state.specValues.find(
          (row) =>
            row.ownerType === ownerType &&
            row.ownerKey === ownerKey &&
            row.mediaType === mediaType &&
            row.property === property,
        );

  return Object.keys(SPEC_PROPERTIES[mediaType]).map((property) => ({
    property,
    own: find('format', format.key, property),
    inherited: find('profile', format.specProfileKey, property),
  }));
}

export interface TextLimitRow {
  field: TextLimitField;
  own: LibraryTextLimit | undefined;
  inherited: LibraryTextLimit | undefined;
}

export function textLimitRows(state: LibraryState, format: LibraryFormat): TextLimitRow[] {
  const find = (ownerType: 'format' | 'profile', ownerKey: string | null, field: string) =>
    ownerKey === null
      ? undefined
      : state.textLimits.find(
          (row) => row.ownerType === ownerType && row.ownerKey === ownerKey && row.field === field,
        );

  return TEXT_LIMIT_FIELDS.map((field) => ({
    field,
    own: find('format', format.key, field),
    inherited: find('profile', format.specProfileKey, field),
  }));
}

/** Groups with their formats, filtered by a search over name, key, aliases and `WxH`. */
export function groupFormats(state: LibraryState, query: string) {
  const needle = query
    .trim()
    .toLowerCase()
    // eslint-disable-next-line i18next/no-literal-string -- the size separator, not copy
    .replace(/\s*x\s*/g, 'x');
  const matches = (format: LibraryFormat) =>
    !needle ||
    [format.name, format.key, ...format.aliases, `${format.w}x${format.h}`].some((term) =>
      term.toLowerCase().includes(needle),
    );

  return (
    [...state.groups]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((group) => ({
        group,
        formats: state.formats
          .filter((format) => format.groupKey === group.key && matches(format))
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
      }))
      // A group with no formats yet stays listed, so it can be given its first one; a search hides it.
      .filter((entry) => entry.formats.length > 0 || !needle)
  );
}
